import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as fileSaver from 'file-saver';
import {
  IInvalidTravelRequest,
  ITravelExpenses,
  ITravelLetter,
  ITravelRequest,
  ITravelRequestDestinationPayload,
  ITravelRequestDetails,
  ITravelRequestPayload,
  ITravelRequestPartial,
  ITravelRequestPrerequisites,
  ITravelRequestTemplateStatus,
} from '../models/travel-request.model';
import { TravelRequestTemplateType } from '../enums/travel-request-template-type.enum';
import { IPagination } from '../models';

@Injectable({
  providedIn: 'root',
})
export class TravelRequestService {
  private httpClient = inject(HttpClient);

  public static readonly DEFAULT_BASE = 'api/business-trip';
  public static readonly TRAVEL_MANAGEMENT_BASE = 'api/travel-management/business-trip';

  private isTravelRequestDeleted = new BehaviorSubject<boolean>(false);

  public deleteFile: (
    employeeId: number,
    businessTripId: number,
    participantId: number,
    fileName,
    baseUrl?: string,
  ) => Observable<any> = (
    employeeId,
    businessTripId,
    participantId,
    fileName,
    baseUrl = TravelRequestService.DEFAULT_BASE,
  ) =>
    this.httpClient.delete<ITravelRequestPartial[]>(
      `${baseUrl}/${employeeId}/${businessTripId}/${participantId}/${fileName}`,
    );

  public createTravelRequest(payload: FormData, baseUrl = TravelRequestService.DEFAULT_BASE): any {
    return this.httpClient.post<any>(`${baseUrl}/create`, payload);
  }

  public createTravelRequestDraft(
    payload: ITravelRequestPayload,
    baseUrl = TravelRequestService.TRAVEL_MANAGEMENT_BASE,
  ): Observable<void> {
    return this.httpClient.post<void>(`${baseUrl}/drafts`, payload);
  }

  public createCompleteTravelRequest(
    trip: ITravelRequestPayload,
    letters: ITravelLetter[],
    baseUrl = TravelRequestService.TRAVEL_MANAGEMENT_BASE,
  ): Observable<number> {
    return this.httpClient.post<number>(`${baseUrl}/create`, { trip, letters });
  }

  public previewTravelRequestLetters(
    payload: ITravelRequestPayload,
    baseUrl = TravelRequestService.TRAVEL_MANAGEMENT_BASE,
  ): Observable<ITravelLetter[]> {
    return this.httpClient.post<ITravelLetter[]>(`${baseUrl}/documents/letters`, payload);
  }

  public getTravelRequestDetails(
    requesterId: number,
    businessTripId: number,
    baseUrl = TravelRequestService.TRAVEL_MANAGEMENT_BASE,
  ): Observable<ITravelRequestDetails> {
    return this.httpClient.get<ITravelRequestDetails>(
      `${baseUrl}/${requesterId}/${businessTripId}`,
    );
  }

  public updateTravelRequestDraft(
    businessTripId: number,
    payload: ITravelRequestPayload,
    baseUrl = TravelRequestService.TRAVEL_MANAGEMENT_BASE,
  ): Observable<void> {
    return this.httpClient.put<void>(`${baseUrl}/drafts/${businessTripId}`, payload);
  }

  public updateCreatedTravelRequest(
    businessTripId: number,
    trip: ITravelRequestPayload,
    letters: ITravelLetter[],
    baseUrl = TravelRequestService.TRAVEL_MANAGEMENT_BASE,
  ): Observable<void> {
    return this.httpClient.put<void>(`${baseUrl}/${businessTripId}`, { trip, letters });
  }

  public saveTravelRequest(
    businessTripId: number | null,
    isCreated: boolean,
    payload: ITravelRequestPayload,
    letters: ITravelLetter[],
  ): Observable<void> {
    if (businessTripId === null) {
      return this.createTravelRequestDraft(payload);
    }

    return isCreated
      ? this.updateCreatedTravelRequest(businessTripId, payload, letters)
      : this.updateTravelRequestDraft(businessTripId, payload);
  }

  public saveTravelRequestDocument(response: HttpResponse<Blob>, fallbackName: string): void {
    fileSaver.saveAs(
      response.body,
      this.extractFileName(response.headers.get('Content-Disposition'), fallbackName),
    );
  }

  public saveFile(content: Blob, fileName: string): void {
    fileSaver.saveAs(content, fileName);
  }

  public downloadDocument(
    businessTripId: number,
    documentId: number,
    baseUrl = TravelRequestService.TRAVEL_MANAGEMENT_BASE,
  ): Observable<Blob> {
    return this.httpClient.get(`${baseUrl}/documents/${businessTripId}/files/${documentId}`, {
      responseType: 'blob',
    });
  }

  public downloadDocumentsArchive(
    businessTripId: number,
    baseUrl = TravelRequestService.TRAVEL_MANAGEMENT_BASE,
  ): Observable<HttpResponse<Blob>> {
    return this.httpClient.get(`${baseUrl}/documents/${businessTripId}/archive`, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  public previewTravelExpenses(
    destinations: ITravelRequestDestinationPayload[],
    baseUrl = TravelRequestService.TRAVEL_MANAGEMENT_BASE,
  ): Observable<ITravelExpenses> {
    return this.httpClient.post<ITravelExpenses>(`${baseUrl}/expenses/preview`, { destinations });
  }

  public getTravelRequests(
    baseUrl = TravelRequestService.DEFAULT_BASE,
  ): Observable<ITravelRequestPartial[]> {
    return this.httpClient.get<ITravelRequestPartial[]>(`${baseUrl}/all`);
  }

  public getFilteredTravelRequests(
    params: HttpParams,
    baseUrl = TravelRequestService.DEFAULT_BASE,
  ): Observable<IPagination<ITravelRequestPartial>> {
    return this.httpClient.get<IPagination<ITravelRequestPartial>>(`${baseUrl}/filtered`, {
      params,
    });
  }

  public getTravelRequestById(
    userId,
    businessTripId,
    baseUrl = TravelRequestService.DEFAULT_BASE,
  ): Observable<ITravelRequest> {
    return this.httpClient.get<ITravelRequest>(`${baseUrl}/${userId}/${businessTripId}`);
  }

  public updateTravelRequest(
    requesterId,
    businessTripId,
    payload,
    baseUrl = TravelRequestService.DEFAULT_BASE,
  ): Observable<any> {
    return this.httpClient.put<ITravelRequest>(
      `${baseUrl}/${requesterId}/${businessTripId}/update`,
      payload,
    );
  }

  public deleteTravelRequest(
    userId,
    businessTripId,
    baseUrl = TravelRequestService.DEFAULT_BASE,
  ): Observable<string> {
    return this.httpClient.delete<string>(`${baseUrl}/${userId}/${businessTripId}`);
  }

  public getMissingTripDocuments(
    userId: number,
    baseUrl = TravelRequestService.DEFAULT_BASE,
  ): Observable<IInvalidTravelRequest> {
    return this.httpClient.get<any>(`${baseUrl}/${userId}/invalid-trips`);
  }

  public getTravelRequestsFilteredByDate(
    fromDate: string,
    toDate: string,
    baseUrl = TravelRequestService.DEFAULT_BASE,
  ): Observable<any> {
    return this.httpClient.get(`${baseUrl}/export?fromDate=${fromDate}&toDate=${toDate}`, {
      responseType: 'arraybuffer',
    });
  }

  public uploadtravelRequestDocuments(
    businessTripId: number,
    participantId: number,
    payload: FormData,
    baseUrl = TravelRequestService.DEFAULT_BASE,
  ) {
    return this.httpClient.post<any>(
      `${baseUrl}/${businessTripId}/${participantId}/upload_documents`,
      payload,
    );
  }

  public getTemplateStatuses(
    baseUrl = TravelRequestService.DEFAULT_BASE,
  ): Observable<ITravelRequestTemplateStatus[]> {
    return this.httpClient.get<ITravelRequestTemplateStatus[]>(`${baseUrl}/templates`);
  }

  public getRequestPrerequisites(
    baseUrl = TravelRequestService.TRAVEL_MANAGEMENT_BASE,
  ): Observable<ITravelRequestPrerequisites> {
    return this.httpClient.get<ITravelRequestPrerequisites>(`${baseUrl}/prerequisites`);
  }

  public uploadTemplate(
    type: TravelRequestTemplateType,
    file: File,
    baseUrl = TravelRequestService.DEFAULT_BASE,
  ): Observable<void> {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post<void>(`${baseUrl}/templates/${type}`, formData);
  }

  public deleteTemplate(
    type: TravelRequestTemplateType,
    baseUrl = TravelRequestService.DEFAULT_BASE,
  ): Observable<void> {
    return this.httpClient.delete<void>(`${baseUrl}/templates/${type}`);
  }

  public downloadTemplate(
    type: TravelRequestTemplateType,
    baseUrl = TravelRequestService.DEFAULT_BASE,
  ): Observable<void> {
    return this.httpClient
      .get(`${baseUrl}/templates/${type}/download`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const fileName = this.extractFileName(
            response.headers.get('Content-Disposition'),
            `${type}.docx`,
          );
          fileSaver.saveAs(response.body, fileName);
        }),
      );
  }

  private extractFileName(contentDisposition: string | null, fallback: string): string {
    if (!contentDisposition) {
      return fallback;
    }
    const utf8Matches = /filename\*=UTF-8''([^;\n]+)/i.exec(contentDisposition);
    if (utf8Matches?.[1]) {
      return decodeURIComponent(utf8Matches[1]);
    }
    const plainMatches = /filename=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
    if (plainMatches?.[1]) {
      return plainMatches[1].replace(/['"]/g, '');
    }
    return fallback;
  }

  setIsTravelRequestDeleted(value: boolean) {
    this.isTravelRequestDeleted.next(value);
  }

  getIsTravelRequestDeleted() {
    return this.isTravelRequestDeleted.asObservable();
  }
}
